import React, { useContext, useCallback, useEffect } from "react";
import { isEmpty } from "lodash";
import moment from "moment";
import { Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { Tooltip } from "components";
import { getFilters } from "store/filters/filters.reducer";
import { FilterColumn } from "types";

import AgeErrorMessage from "./ageErrorMessage";
import DayHourMinuteSection from "./dayHourMinuteSection";
import FiltersContext from "../filtersContext";

import styles from "../filter.module.scss";


type FilterColumnProps = {
	column: FilterColumn;
	className?: string;
};

const FilterDayHourMinuteInput = ({ column }: FilterColumnProps) => {
	const { setIsApplyDisabled } = useContext(FiltersContext);
	const { watch, formState, register } = useFormContext();

	const formValues = useSelector(getFilters(column.formName)) || {};

	const dayFrom = watch(`${column.dayFrom}`);
	const hoursFrom = watch(`${column.hoursFrom}`);
	const minFrom = watch(`${column.minFrom}`);

	const dayTo = watch(`${column.dayTo}`);
	const hoursTo = watch(`${column.hoursTo}`);
	const minTo = watch(`${column.minTo}`);

	const dayFromStore = formValues[column?.dayFrom || ""];
	const hoursFromStore = formValues[column?.hoursFrom || ""];
	const minFromStore = formValues[column?.minFrom || ""];

	const dayToStore = formValues[column?.dayTo || ""];
	const hoursToStore = formValues[column?.hoursTo || ""];
	const minToStore = formValues[column?.minTo || ""];

	const isEmptyAgeMinValues = useCallback(() => {
		return isEmpty(dayFrom) && isEmpty(hoursFrom) && isEmpty(minFrom);
	}, [dayFrom, hoursFrom, minFrom]);

	const isEmptyAgeMaxValues = useCallback(() => {
		return isEmpty(dayTo) && isEmpty(hoursTo) && isEmpty(minTo);
	}, [dayTo, hoursTo, minTo]);

	const isEmptyAgeMinValuesStore = useCallback(() => {
		return (
			isEmpty(dayFromStore) &&
			isEmpty(hoursFromStore) &&
			isEmpty(minFromStore)
		);
	}, [dayFromStore, hoursFromStore, minFromStore]);

	const isEmptyAgeMaxValuesStore = useCallback(() => {
		return (
			isEmpty(dayToStore) && isEmpty(hoursToStore) && isEmpty(minToStore)
		);
	}, [dayToStore, hoursToStore, minToStore]);

	const durationAgeMinimumFrom = moment(Date.now())
		.subtract({
			days: parseInt(dayFrom),
			hours: parseInt(hoursFrom),
			minutes: parseInt(minFrom),
		})
		.format();

	const durationAgeMinimumTo = moment(Date.now())
		.subtract({
			days: parseInt(dayTo),
			hours: parseInt(hoursTo),
			minutes: parseInt(minTo),
		})
		.format();

	const durationAgeMinimumFromStore = moment(Date.now())
		.subtract({
			days: parseInt(dayFromStore),
			hours: parseInt(hoursFromStore),
			minutes: parseInt(minFromStore),
		})
		.format();

	const durationAgeMinimumToStore = moment(Date.now())
		.subtract({
			days: parseInt(dayToStore),
			hours: parseInt(hoursToStore),
			minutes: parseInt(minToStore),
		})
		.format();

	const isDurationInvalid = useCallback(() => {
		let isInvalid = false;

		if (!isEmptyAgeMinValuesStore() && !isEmptyAgeMaxValues()) {
			isInvalid =
				Date.parse(durationAgeMinimumFromStore) <
				Date.parse(durationAgeMinimumTo);
		}

		if (!isEmptyAgeMaxValuesStore() && !isEmptyAgeMinValues()) {
			isInvalid =
				Date.parse(durationAgeMinimumFrom) <
				Date.parse(durationAgeMinimumToStore);
		}

		if (!isEmptyAgeMinValues() && !isEmptyAgeMaxValues()) {
			isInvalid =
				Date.parse(durationAgeMinimumFrom) <
				Date.parse(durationAgeMinimumTo);
		}

		setIsApplyDisabled?.(isInvalid);

		return isInvalid;
	}, [
		isEmptyAgeMinValuesStore,
		isEmptyAgeMaxValuesStore,
		isEmptyAgeMaxValues,
		isEmptyAgeMinValues,
		setIsApplyDisabled,
		durationAgeMinimumFrom,
		durationAgeMinimumTo,
		durationAgeMinimumFromStore,
		durationAgeMinimumToStore,
	]);

	useEffect(() => {
		// If errors, disable apply
		setIsApplyDisabled?.(!isEmpty(formState.errors));
	}, [formState.errors, setIsApplyDisabled]);

	return (
		<div className={styles.column}>
			<Tooltip
				trigger="click"
				position="bottom"
				interactive
				html={
					<div className={styles.agePickerContent}>
						<DayHourMinuteSection
							register={register}
							name={column.name}
							section={{
								days: {
									name: column.dayName,
									normalize: column.daysNormalize,
								},
								hours: {
									name: column.hoursName,
									normalize: column.hoursNormalize,
								},
								minutes: {
									name: column.minName,
									normalize: column.minutesNormalize,
								},
							}}
						/>
						{isDurationInvalid() && <AgeErrorMessage />}
					</div>
				}
			>
				<Form.Control
					name={column.name}
					placeholder={column.placeholder}
					className={styles.input}
					readOnly
				/>
			</Tooltip>
		</div>
	);
};

export default FilterDayHourMinuteInput;
